import React, { useState } from 'react'
import { Card, Statistic, Button, Space, Tag, Modal, Form, InputNumber, Input, message, Tooltip } from 'antd'
import { WalletOutlined, PlusOutlined, InfoCircleOutlined } from '@ant-design/icons'
import type { Wallet } from '../types/wallet.types'
import { walletsService } from '../../../shared/api/services/wallets.service'

interface WalletCardProps {
    wallet: Wallet
    onUpdate: () => void
}

export const WalletCard: React.FC<WalletCardProps> = ({ wallet, onUpdate }) => {
    const [isModalVisible, setIsModalVisible] = useState(false)
    const [isCreditLimitModalVisible, setIsCreditLimitModalVisible] = useState(false)
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [isLimitSubmitting, setIsLimitSubmitting] = useState(false)
    const [form] = Form.useForm()
    const [limitForm] = Form.useForm()

    const getBiddingBlockReasons = (w: Wallet): string[] => {
        if (w.can_bid) return []

        const minBiddingBalance = w.min_bidding_balance ?? 0
        const reasons: string[] = []
        if (w.status !== 'active') {
            reasons.push(`Wallet status is '${w.status.toUpperCase()}'`)
        }
        const biddingPower = (w.available_balance || 0) + (w.credit_limit || 0)
        if (biddingPower < minBiddingBalance) {
            reasons.push(`Available balance + credit limit (${w.currency} ${biddingPower.toFixed(2)}) is below the minimum bidding balance of ${w.currency} ${minBiddingBalance.toFixed(2)}`)
        }
        if (reasons.length === 0) {
            reasons.push('Bidding is disabled for this merchant')
        }
        return reasons
    }

    const biddingBlockReasons = getBiddingBlockReasons(wallet)

    const handleCredit = async (values: any) => {
        try {
            setIsSubmitting(true)
            await walletsService.creditWallet(wallet.merchant_id.toString(), {
                amount: values.amount,
                description: values.description,
                reference_id: values.reference_id
            })
            message.success('Wallet credited successfully')
            setIsModalVisible(false)
            form.resetFields()
            onUpdate()
        } catch (error: any) {
            message.error(error.message || 'Failed to credit wallet')
        } finally {
            setIsSubmitting(false)
        }
    }

    const handleSetCreditLimit = async (values: any) => {
        try {
            setIsLimitSubmitting(true)
            await walletsService.updateCreditLimit(wallet.merchant_id.toString(), values.credit_limit)
            message.success('Credit limit updated successfully')
            setIsCreditLimitModalVisible(false)
            limitForm.resetFields()
            onUpdate()
        } catch (error: any) {
            message.error(error.message || 'Failed to update credit limit')
        } finally {
            setIsLimitSubmitting(false)
        }
    }

    return (
        <Card>
            <Space direction="vertical" size="large" style={{ width: '100%' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '16px' }}>
                    <div style={{ display: 'flex', gap: '32px', flexWrap: 'wrap' }}>
                        <Statistic
                            title={
                                <Space size={4}>
                                    <span>Wallet Balance</span>
                                    <Tooltip title="Actual money recharged by the merchant. Subscriptions and bidding fees are debited directly from this amount.">
                                        <InfoCircleOutlined style={{ fontSize: '12px', color: '#8c8c8c', cursor: 'help' }} />
                                    </Tooltip>
                                </Space>
                            }
                            value={wallet.balance}
                            precision={2}
                            prefix={<WalletOutlined />}
                            suffix={wallet.currency}
                            valueStyle={{ color: wallet.balance < 0 ? '#cf1322' : '#3f8600' }}
                        />
                        <Statistic
                            title={
                                <Space size={4}>
                                    <span>Reserved Balance</span>
                                    <Tooltip title="Funds put on hold for pending subscription upgrades or active premium radius services. These funds cannot be used for new bids.">
                                        <InfoCircleOutlined style={{ fontSize: '12px', color: '#8c8c8c', cursor: 'help' }} />
                                    </Tooltip>
                                </Space>
                            }
                            value={wallet.reserved_balance || 0}
                            precision={2}
                            suffix={wallet.currency}
                            valueStyle={{ color: '#fa8c16' }}
                        />
                        <Statistic
                            title={
                                <Space size={4}>
                                    <span>Credit Limit</span>
                                    <Tooltip title="The maximum allowed overdraft limit granted to this merchant. Enables continued bidding even with a negative balance.">
                                        <InfoCircleOutlined style={{ fontSize: '12px', color: '#8c8c8c', cursor: 'help' }} />
                                    </Tooltip>
                                </Space>
                            }
                            value={wallet.credit_limit || 0}
                            precision={2}
                            suffix={wallet.currency}
                            valueStyle={{ color: '#1890ff' }}
                        />
                        <Statistic
                            title={
                                <Space size={4}>
                                    <span>Available Balance</span>
                                    <Tooltip title="Total usable bidding power. Calculated as: (Wallet Balance + Credit Limit) - Reserved Balance.">
                                        <InfoCircleOutlined style={{ fontSize: '12px', color: '#8c8c8c', cursor: 'help' }} />
                                    </Tooltip>
                                </Space>
                            }
                            value={wallet.available_balance || 0}
                            precision={2}
                            suffix={wallet.currency}
                            valueStyle={{ color: '#52c41a', fontWeight: 'bold' }}
                        />
                    </div>
                    <Space direction="vertical" align="end">
                        <Tag color={wallet.status === 'active' ? 'green' : 'red'}>
                            {wallet.status.toUpperCase()}
                        </Tag>
                        {wallet.can_bid ? (
                            <Tag color="blue">
                                BIDDING ENABLED
                            </Tag>
                        ) : (
                            <Tooltip
                                title={
                                    <ul style={{ margin: 0, paddingLeft: 16 }}>
                                        {biddingBlockReasons.map((reason, i) => (
                                            <li key={i}>{reason}</li>
                                        ))}
                                    </ul>
                                }
                            >
                                <Tag color="orange" style={{ cursor: 'help' }}>
                                    BIDDING BLOCKED
                                </Tag>
                            </Tooltip>
                        )}
                    </Space>
                </div>

                <Space>
                    <Button
                        type="primary"
                        icon={<PlusOutlined />}
                        onClick={() => setIsModalVisible(true)}
                    >
                        Add Fund (Manual Credit)
                    </Button>
                    <Button
                        onClick={() => {
                            limitForm.setFieldsValue({ credit_limit: wallet.credit_limit || 0 })
                            setIsCreditLimitModalVisible(true)
                        }}
                    >
                        Set Credit Limit
                    </Button>
                </Space>
            </Space>

            <Modal
                title="Manual Wallet Credit"
                open={isModalVisible}
                onCancel={() => setIsModalVisible(false)}
                onOk={() => form.submit()}
                confirmLoading={isSubmitting}
                destroyOnHidden
            >
                <Form
                    form={form}
                    layout="vertical"
                    onFinish={handleCredit}
                    initialValues={{ amount: 100 }}
                >
                    <Form.Item
                        name="amount"
                        label="Amount"
                        rules={[{ required: true, message: 'Please enter amount' }]}
                    >
                        <InputNumber
                            style={{ width: '100%' }}
                            min={0.01}
                            placeholder="Enter amount to credit"
                        />
                    </Form.Item>
                    <Form.Item
                        name="description"
                        label="Description"
                        rules={[{ required: true, message: 'Please enter description' }]}
                    >
                        <Input placeholder="E.g. Cash Top-up, Bonus, Adjustment" />
                    </Form.Item>
                    <Form.Item
                        name="reference_id"
                        label="Reference ID (Optional)"
                    >
                        <Input placeholder="E.g. Transaction ID, Receipt No" />
                    </Form.Item>
                </Form>
            </Modal>

            <Modal
                title="Set Credit Limit"
                open={isCreditLimitModalVisible}
                onCancel={() => setIsCreditLimitModalVisible(false)}
                onOk={() => limitForm.submit()}
                confirmLoading={isLimitSubmitting}
                destroyOnHidden
            >
                <Form
                    form={limitForm}
                    layout="vertical"
                    onFinish={handleSetCreditLimit}
                >
                    <Form.Item
                        name="credit_limit"
                        label="Credit Limit (Overdraft)"
                        rules={[{ required: true, message: 'Please enter credit limit' }]}
                    >
                        <InputNumber
                            style={{ width: '100%' }}
                            min={0}
                            placeholder="Enter overdraft credit limit"
                        />
                    </Form.Item>
                </Form>
            </Modal>
        </Card>
    )
}
